import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MEDIA, RESTAURANT } from "../lib/api";
import { useEffect, useState } from "react";
import { api, formatPrice } from "../lib/api";
import PartnersBar from "../components/PartnersBar";

export default function Home() {
  const [featured, setFeatured] = useState([]);

 useEffect(() => {
  api.get("/menu")
    .then((r) => {
      if (r?.data) {
        setFeatured(r.data.slice(0, 3));
      }
    })
    .catch((err) => {
      console.log("Menu fetch failed:", err);
      setFeatured([]);
    });
}, []);

  return (
    <div data-testid="home-page">
      {/* Hero — exterior */}
      <section className="relative h-screen min-h-[640px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={MEDIA.hero}
            alt="CHICA Dehradun exterior"
            className="w-full h-full object-cover"
          />
          {/* Top fade for header legibility */}
          <div className="absolute inset-x-0 top-0 h-48 bg-gradient-to-b from-[#0F120E]/85 to-transparent" />
          {/* Bottom fade for headline legibility */}
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0F120E]/30 to-[#0F120E]" />
        </div>

        <div className="relative w-full max-w-[1400px] mx-auto px-6 md:px-12 pb-24 z-10">
          <h1
            data-testid="hero-title"
            className="font-display text-6xl sm:text-7xl lg:text-[120px] leading-[0.92] tracking-tight text-[#F9F6F0] max-w-4xl fade-up"
          >
            A taste,
            <br />
            <em className="text-[#D4C1A0] not-italic">an evening</em>,
            <br />
            a feeling.
          </h1>
          <p className="mt-8 max-w-md text-[#F9F6F0]/80 leading-relaxed fade-up">
            Fine dining, lounge and nightclub on Rajpur Road — where North Indian,
            Italian, Asian and Continental cuisines meet in a Pinterest-worthy
            social hub.
          </p>
          <div className="flex flex-wrap gap-4 mt-12 fade-up">
            <Link to="/reserve" data-testid="hero-reserve-cta" className="btn-line">
              Reserve a table <ArrowRight className="w-4 h-4" strokeWidth={1.2} />
            </Link>
            <Link to="/menu" data-testid="hero-menu-cta" className="btn-line">
              View menu
            </Link>
          </div>
        </div>

        <div className="absolute bottom-8 right-28 hidden md:block text-right">
          <div className="overline">Open daily</div>
          <div className="text-sm text-[#9AA894] mt-1">12:00 — 22:00</div>
        </div>
      </section>

      {/* Story */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-32 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5 md:col-start-2">
          <div className="overline mb-6">Our story</div>
          <h2 className="font-display text-4xl sm:text-5xl text-[#F9F6F0] leading-tight">
            A premium social hub<br />on Rajpur Road.
          </h2>
        </div>
        <div className="md:col-span-5 md:col-start-8">
          <p className="text-[#9AA894] leading-relaxed">
            CHICA Dehradun is a popular premium fine-dining restaurant, lounge,
            and nightclub located on Rajpur Road. It is well-regarded for its
            vibrant atmosphere, "Pinterest-worthy" aesthetic, and an expansive
            menu that covers North Indian, Italian, Asian, and Continental cuisines.
          </p>
          <Link to="/about" data-testid="home-about-link" className="btn-line mt-10 inline-flex">
            Read our story
          </Link>
        </div>
      </section>

      {/* Featured menu */}
      <section className="bg-[#1C2219]/40 py-32">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-16">
            <div>
              <div className="overline mb-4">Tonight's selection</div>
              <h2 className="font-display text-4xl sm:text-5xl text-[#F9F6F0]">
                Signature dishes from the kitchen
              </h2>
            </div>
            <Link to="/menu" data-testid="home-full-menu-link" className="hidden md:inline-flex btn-line">
              Full menu
            </Link>
          </div>

          <div className="space-y-1" data-testid="featured-menu">
            {Array.isArray(featured) && featured.map((item) => (
              <div
                key={item.id}
                data-testid={`featured-item-${item.id}`}
                className="flex items-baseline py-7 border-b border-[#2E3628]/60"
              >
                <div className="flex-1">
                  <h3 className="font-display text-2xl text-[#F9F6F0]">{item.name}</h3>
                  <p className="text-[#9AA894] text-sm mt-2 max-w-xl leading-relaxed">{item.description}</p>
                </div>
                <div className="menu-dots hidden md:block" />
                <div className="font-display text-xl text-[#D4C1A0] ml-4">{formatPrice(item.price)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reserve CTA */}
      <section className="relative h-[480px] flex items-center justify-center my-32 overflow-hidden">
        <div className="absolute inset-0 img-zoom">
          <img src={MEDIA.interior} alt="Interior" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#0F120E]/70" />
        </div>
        <div className="relative z-10 text-center px-6">
          <div className="overline mb-6 text-[#E6D5D6]">An evening reserved · 15% off via chica.com</div>
          <h2 className="font-display text-5xl sm:text-6xl text-[#F9F6F0] max-w-3xl mx-auto leading-tight">
            We hold a small number of tables each evening.
          </h2>
          <Link to="/reserve" data-testid="cta-reserve-btn" className="btn-solid mt-10 inline-flex">
            Reserve your table
          </Link>
        </div>
      </section>

      <PartnersBar />

      {/* Gallery preview */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-32">
        <div className="grid grid-cols-12 gap-3 md:gap-6">
          <div className="col-span-12 md:col-span-8 img-zoom aspect-[16/10]">
            <img
              src={MEDIA.galleryFeature}
              alt="CHICA outdoor dining"
              className="w-full h-full object-cover object-[center_60%]"
            />
          </div>
          <div className="col-span-6 md:col-span-4 img-zoom aspect-[4/5]">
            <img src={MEDIA.dish} alt="Indian spread" className="w-full h-full object-cover" />
          </div>
          <div className="col-span-6 md:col-span-4 img-zoom aspect-[4/5]">
            <img src={MEDIA.cocktail} alt="Cocktail" className="w-full h-full object-cover" />
          </div>
          <div className="col-span-12 md:col-span-8 flex items-center justify-center bg-[#1C2219] border border-[#2E3628] p-12">
            <div className="text-center">
              <div className="overline mb-4">Visit the gallery</div>
              <Link to="/gallery" data-testid="home-gallery-link" className="font-display text-4xl text-[#F9F6F0] hover:text-[#D4C1A0]">
                See more →
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
